<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, useId } from 'vue'
import { AlertCircle, Maximize2, Pause, Play, Volume2, VolumeX, LoaderCircle } from 'lucide-vue-next'
import type { DemoVideo } from '@/data/videoDemo'
import { getVideoHomeCopy, type HomeLocale } from '@/data/videoHomeCopy'

const props = withDefaults(defineProps<{
  video: DemoVideo
  mode?: 'hero' | 'hover' | 'click'
  autoplay?: boolean
  loop?: boolean
  previewLimit?: number
  locale?: HomeLocale
}>(), {
  mode: 'hover',
  autoplay: false,
  loop: true,
  previewLimit: 8,
  locale: 'zh',
})

const emit = defineEmits<{ open: [video: DemoVideo] }>()
const videoElement = ref<HTMLVideoElement | null>(null)
const container = ref<HTMLElement | null>(null)
const sourceAttached = ref(false)
const loaded = ref(false)
const playing = ref(false)
const muted = ref(true)
const failed = ref(false)
const visible = ref(false)
const instanceId = useId()
let observer: IntersectionObserver | null = null

const ratioClass = computed(() => `ratio-${props.video.ratio.replace(':', '-')}`)
const copy = computed(() => getVideoHomeCopy(props.locale).video)
const shouldAutoplay = () => props.autoplay && !window.matchMedia('(max-width: 768px)').matches
const supportsHover = () => window.matchMedia('(hover: hover) and (pointer: fine)').matches

const attachSource = () => {
  if (sourceAttached.value || !videoElement.value) return
  sourceAttached.value = true
  videoElement.value.src = props.video.src
  videoElement.value.load()
}

const pause = (reset = false) => {
  const element = videoElement.value
  if (!element) return
  element.pause()
  playing.value = false
  if (reset && Number.isFinite(element.duration)) {
    element.currentTime = 0
  }
}

const play = async () => {
  const element = videoElement.value
  if (!element || failed.value) return
  attachSource()
  window.dispatchEvent(new CustomEvent('nova:video-play', { detail: instanceId }))
  try {
    await element.play()
    playing.value = true
  } catch {
    playing.value = false
  }
}

const togglePlay = () => {
  if (playing.value) pause()
  else void play()
}

const toggleMute = () => {
  const element = videoElement.value
  if (!element) return
  element.muted = !element.muted
  muted.value = element.muted
}

const requestFullscreen = async () => {
  const element = videoElement.value
  if (!element) return
  if (!element.requestFullscreen) return
  try {
    await element.requestFullscreen()
  } catch {
    // 浏览器拒绝全屏时保留当前播放状态。
  }
}

const onEnter = () => {
  if (props.mode !== 'hover' || !supportsHover()) return
  const element = videoElement.value
  attachSource()
  if (element) element.currentTime = 0
  void play()
}

const onLeave = () => {
  if (props.mode === 'hover' && supportsHover()) pause(true)
}

const onTimeUpdate = () => {
  const element = videoElement.value
  if (!element || !Number.isFinite(element.duration) || element.duration === 0) return
  if (props.mode === 'hover' && element.currentTime >= Math.min(props.previewLimit, element.duration)) pause(true)
}

const onGlobalVideoPlay = (event: Event) => {
  const currentId = (event as CustomEvent<string>).detail
  if (currentId !== instanceId) pause()
}

const onContainerClick = () => {
  if (props.mode === 'click') emit('open', props.video)
}

onMounted(() => {
  window.addEventListener('nova:video-play', onGlobalVideoPlay)
  if (props.mode === 'hero') attachSource()

  observer = new IntersectionObserver((entries) => {
    const entry = entries[0]
    visible.value = Boolean(entry?.isIntersecting)
    if (entry?.isIntersecting) {
      attachSource()
      if (shouldAutoplay()) void play()
    } else {
      pause()
    }
  }, { rootMargin: '220px 0px', threshold: .22 })

  if (container.value) observer.observe(container.value)
})

onBeforeUnmount(() => {
  observer?.disconnect()
  window.removeEventListener('nova:video-play', onGlobalVideoPlay)
})
</script>

<template>
  <div
    ref="container"
    class="demo-video"
    :class="[ratioClass, `mode-${mode}`, { 'is-playing': playing, 'is-loaded': loaded, 'has-failed': failed }]"
    @mouseenter="onEnter"
    @mouseleave="onLeave"
    @click="onContainerClick"
  >
    <video
      ref="videoElement"
      :poster="video.poster"
      :muted="muted"
      :loop="loop"
      :preload="mode === 'hero' ? 'metadata' : 'none'"
      playsinline
      disablepictureinpicture
      @loadeddata="loaded = true"
      @canplay="shouldAutoplay() && visible && play()"
      @play="playing = true"
      @pause="playing = false"
      @timeupdate="onTimeUpdate"
      @error="failed = true"
    />

    <img v-if="failed" class="fallback-image" :src="video.poster" :alt="`${video.title} ${copy.fallbackCover}`" />

    <div v-if="sourceAttached && !loaded && !failed" class="video-state loading-state" aria-live="polite">
      <LoaderCircle :size="26" :stroke-width="1.8" aria-hidden="true" />
      <span>{{ copy.loading }}</span>
    </div>
    <div v-if="failed" class="video-state failed-state" role="status">
      <AlertCircle :size="24" :stroke-width="1.8" aria-hidden="true" />
      <span>{{ copy.loadFailed }}</span>
    </div>

    <button v-if="!playing && !failed" class="center-play" type="button" :aria-label="`${copy.play} ${video.title}`" @click.stop="togglePlay">
      <Play :size="24" fill="currentColor" aria-hidden="true" />
    </button>

    <div class="video-controls" @click.stop>
      <button type="button" :aria-label="playing ? copy.pause : copy.playVideo" @click="togglePlay">
        <Pause v-if="playing" :size="18" fill="currentColor" aria-hidden="true" />
        <Play v-else :size="18" fill="currentColor" aria-hidden="true" />
      </button>
      <button type="button" :aria-label="muted ? copy.unmute : copy.mute" @click="toggleMute">
        <VolumeX v-if="muted" :size="18" aria-hidden="true" />
        <Volume2 v-else :size="18" aria-hidden="true" />
      </button>
      <span class="duration">{{ video.duration }}</span>
      <button type="button" :aria-label="copy.fullscreen" @click="requestFullscreen">
        <Maximize2 :size="18" aria-hidden="true" />
      </button>
    </div>
  </div>
</template>

<style scoped>
.demo-video {
  position: relative;
  width: 100%;
  height: 100%;
  overflow: hidden;
  color: #fff;
  background: #111820;
  isolation: isolate;
}
.demo-video video,
.fallback-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.fallback-image { position: absolute; inset: 0; z-index: 1; }
.center-play {
  position: absolute;
  z-index: 5;
  left: 50%;
  top: 50%;
  width: 62px;
  height: 62px;
  display: grid;
  place-items: center;
  color: #fff;
  border: 1px solid rgba(255,255,255,.75);
  border-radius: 50%;
  background: rgba(3, 8, 12, .34);
  backdrop-filter: blur(10px);
  transform: translate(-50%, -50%);
  transition: transform .2s ease, background .2s ease;
}
.center-play:hover { transform: translate(-50%, -50%) scale(1.06); background: rgba(3, 8, 12, .52); }
.video-controls {
  position: absolute;
  z-index: 6;
  left: 16px;
  width: fit-content;
  max-width: calc(100% - 32px);
  bottom: 14px;
  min-height: 42px;
  padding: 7px 9px;
  display: flex;
  align-items: center;
  gap: 8px;
  border: 1px solid rgba(255,255,255,.14);
  border-radius: 999px;
  background: rgba(2, 7, 11, .62);
  backdrop-filter: blur(14px);
  opacity: 0;
  transform: translateY(8px);
  transition: opacity .2s ease, transform .2s ease;
}
.demo-video:hover .video-controls,
.mode-hero .video-controls,
.demo-video:focus-within .video-controls { opacity: 1; transform: translateY(0); }
.video-controls button {
  width: 30px;
  height: 30px;
  display: grid;
  place-items: center;
  flex: 0 0 auto;
  padding: 0;
  color: #fff;
  border: 0;
  border-radius: 50%;
  background: transparent;
}
.video-controls button:hover { background: rgba(255,255,255,.12); }
.duration { color: rgba(255,255,255,.72); font-size: 10px; font-variant-numeric: tabular-nums; }
.video-state {
  position: absolute;
  z-index: 4;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  color: rgba(255,255,255,.82);
  background: rgba(4, 9, 14, .38);
  font-size: 12px;
}
.loading-state svg { animation: spin .9s linear infinite; }
.failed-state { background: rgba(4, 9, 14, .56); }
.mode-click { cursor: zoom-in; }
.mode-click .video-controls { display: none; }
.mode-click .center-play { pointer-events: none; }
@keyframes spin { to { transform: rotate(360deg); } }
@media (max-width: 768px) {
  .video-controls { opacity: 1; transform: none; }
  .mode-hover { cursor: pointer; }
}
@media (prefers-reduced-motion: reduce) {
  .center-play, .video-controls { transition: none; }
  .loading-state svg { animation: none; }
}
</style>
